/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICheckoutGuestProfileViewProps } from './checkout-guest-profile';
import { IAddContactInfo } from './components/get-add-contact-info';
import { IAddCustomerRefInfo } from './components/get-add-customer-reference-info';
import { IAddPurchaseOrderInfo } from './components/get-add-customer-requisition-info';
import { IAddNoteInfo } from './components/get-add-note-info';
import { IShowContactInfo } from './components/get-show-contact-info';
import { IShowCustomerRefInfo } from './components/get-show-customer-reference-info';
import { IShowPurchaseOrderInfo } from './components/get-show-customer-requisition-info';
import { IShowNoteInfo } from './components/get-show-note-info';

export const ShowContactInfo: React.FC<IShowContactInfo> = ({ showInfoProps, email, editButton, label }) => (
    <Node {...showInfoProps}>
        {label}
        {email}
        {editButton}
    </Node>
);

export const ShowCustomerRefInfo: React.FC<IShowCustomerRefInfo> = ({ showInfoProps, customerRef, label }) => (
    <Node {...showInfoProps}>
        {label}
        {customerRef}
    </Node>
);

export const ShowPurchaseOrderInfo: React.FC<IShowPurchaseOrderInfo> = ({ showInfoProps, purchaseOrder, label }) => (
    <Node {...showInfoProps}>
        {label}
        {purchaseOrder}
    </Node>
);

export const ShowNoteInfo: React.FC<IShowNoteInfo> = ({ showInfoProps, noteDescription, label }) => (
    <Node {...showInfoProps}>
        {label}
        {noteDescription}
    </Node>
);

export const AddContactInfo: React.FC<IAddContactInfo> = ({
    addFormProps,
    submitButton,
    cancelButton,
    inputGroupProps,
    label,
    error,
    input
}) => (
    <Node {...addFormProps}>
        <Node {...inputGroupProps}>
            {label}
            {error}
            {input}
        </Node>
        {submitButton}
        {cancelButton}
    </Node>
);

export const AddPurchaseOrderInfo: React.FC<IAddPurchaseOrderInfo> = ({
    addFormProps,
    submitButton,
    cancelButton,
    inputGroupProps,
    label,
    input
}) => (
    <Node {...addFormProps}>
        <Node {...inputGroupProps}>
            {label}
            {input}
        </Node>
        {submitButton}
        {cancelButton}
    </Node>
);

export const AddNoteInfo: React.FC<IAddNoteInfo> = ({ addFormProps, submitButton, cancelButton, inputGroupProps, label, input }) => (
    <Node {...addFormProps}>
        <Node {...inputGroupProps}>
            {label}
            {input}
        </Node>
        {submitButton}
        {cancelButton}
    </Node>
);

export const AddCustomerRefInfo: React.FC<IAddCustomerRefInfo> = ({
    addFormProps,
    submitButton,
    cancelButton,
    inputGroupProps,
    label,
    input
}) => (
    <Node {...addFormProps}>
        <Node {...inputGroupProps}>
            {label}
            {input}
        </Node>
        {submitButton}
        {cancelButton}
    </Node>
);

const CheckoutGuestProfileView: React.FC<ICheckoutGuestProfileViewProps> = ({
    moduleProps,
    checkoutErrorRef,
    alert,
    showContactInfo,
    showPurchaseOrderInfo,
    showCustomerRefInfo,
    showNoteInfo,
    addContactInfo,
    addPurchaseOrderInfo,
    addCustomerRefInfo,
    addNoteInfo,
    onEdit
}) => {
    const isShowMode =
        !!(showContactInfo || showCustomerRefInfo || showPurchaseOrderInfo || showNoteInfo) &&
        !addContactInfo &&
        !addCustomerRefInfo &&
        !addPurchaseOrderInfo &&
        !addNoteInfo;

    return (
        <Module {...moduleProps} ref={checkoutErrorRef}>
            {alert}
            {isShowMode && onEdit ? (
                <div
                    className='ms-checkout-guest-profile__show-wrapper'
                    onClick={onEdit}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            onEdit();
                        }
                    }}
                >
                    {showContactInfo && <ShowContactInfo {...showContactInfo} />}
                    {showCustomerRefInfo && <ShowCustomerRefInfo {...showCustomerRefInfo} />}
                    {showPurchaseOrderInfo && <ShowPurchaseOrderInfo {...showPurchaseOrderInfo} />}
                    {showNoteInfo && <ShowNoteInfo {...showNoteInfo} />}
                </div>
            ) : (
                <>
                    {showContactInfo && <ShowContactInfo {...showContactInfo} />}
                    {showCustomerRefInfo && <ShowCustomerRefInfo {...showCustomerRefInfo} />}
                    {showPurchaseOrderInfo && <ShowPurchaseOrderInfo {...showPurchaseOrderInfo} />}
                    {showNoteInfo && <ShowNoteInfo {...showNoteInfo} />}
                </>
            )}
            {addContactInfo && <AddContactInfo {...addContactInfo} />}
            {addCustomerRefInfo && <AddCustomerRefInfo {...addCustomerRefInfo} />}
            {addPurchaseOrderInfo && <AddPurchaseOrderInfo {...addPurchaseOrderInfo} />}
            {addNoteInfo && <AddNoteInfo {...addNoteInfo} />}
        </Module>
    );
};

export default CheckoutGuestProfileView;
